import React, {useEffect, useState} from 'react';
import {Table, Select, Input, Checkbox, DatePicker, Space, Button, message, Modal, Tooltip, Tag} from 'antd';
import {
    UndoOutlined,
    SaveOutlined,
    FileOutlined,
    FileAddOutlined,
    CopyOutlined,
    QuestionOutlined,
    WarningOutlined,
    CodeSandboxOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import {getConfig, isTestFeatureEnabled} from "../../../config";
import {getToken} from "../../../security/tokenStorage";
import {useProjectContext} from "../../../context/ProjectContext";
import {JobCode} from "../../../types/JobCodes";
import {useDirtyContext} from "../../../context/DirtyContext";
import './JobCodeConfiguration.css';
import locale from "antd/es/date-picker/locale/de_DE";

const { Option } = Select;
const { API_BASE_URL, KALKULATION_APP_URL } = getConfig();
// „Produkt konfigurieren"-Wizard nur auf Test/Dev zeigen, nicht produktiv (hostname-basiert).
const SHOW_PRODUCT_CONFIG_BUTTON = isTestFeatureEnabled();

const JobCodeConfiguration: React.FC = () => {
    const [data, setData] = useState<JobCode[]>([]);
    const [originalData, setOriginalData] = useState<JobCode[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isNewProductModalVisible, setIsNewProductModalVisible] = useState(false);
    const { selectedProject } = useProjectContext();
    const [selectedRecord, setSelectedRecord] = useState<JobCode | null>(null);
    const { setDirty } = useDirtyContext();
    const [isSavingNewProduct, setIsSavingNewProduct] = useState(false);
    const [isSystemPlanSaving, setIsSystemPlanSaving] = useState(false);
    const [isSystemPlanUploadModalVisible, setIsSystemPlanUploadModalVisible] = useState(false);
    const [heliosSystemPlansUrl, setHeliosSystemPlansUrl] = useState<string>('');
    const [releaseDate, setReleaseDate] = useState<any>(null);
    const [isIFrameModalVisible, setIsIFrameModalVisible] = useState(false);

    const [newProduct, setNewProduct] = useState<Partial<JobCode>>({
        alias: '',
        description: '',
        projectManagerHours: 0,
        productOwnerHours: 0,
        drawingHours: 0,
        assemblyDays: 0,
        hasSystemPlanning: '0',
        drawByTheory: '0',
        externalProduct: '0',
        galvanized: '0',
        hasSurface: '0',
        preAssembly: '0',
        assemblyType: 'Eigene Montage',
        installationLocation: 'Innen',
        colorCode: '',
    });

    const openWizardModal = (record: JobCode) => {
        setSelectedRecord(record);
        setIsIFrameModalVisible(true);
    };

    const fetchData = async (projectNo: string) => {
        setIsLoading(true);
        try {
            const response = await axios.get<JobCode[]>(
                `${API_BASE_URL}/getNetworkPlanJobCodes?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&includeAll=true`
            );
            setData(response.data);
            setOriginalData(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Daten.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProject) {
            fetchData(selectedProject);
        }
    }, [selectedProject]);

    useEffect(() => {
        const handleIFrameMessage = (event: MessageEvent) => {
            if (event.data && event.data.type === 'EXECUTION_SAVE_SUCCESS') {
                const savedJobCode = event.data.jobCode;
                setIsIFrameModalVisible(false);
                message.success(`Konfiguration für ${savedJobCode} erfolgreich gespeichert.`);
                if (selectedProject) {
                    fetchData(selectedProject);
                }
            }
        };
        window.addEventListener('message', handleIFrameMessage);
        return () => {
            window.removeEventListener('message', handleIFrameMessage);
        };
    }, [selectedProject]);

    const isCellChanged = (record: JobCode, key: keyof JobCode): boolean => {
        const original = originalData.find(item => item.alias === record.alias);
        return original ? original[key] !== record[key] : false;
    };

    const handleDropdownChange = (value: string, record: JobCode, key: "assemblyType" | "installationLocation") => {
        setDirty(true);
        setData((prev) =>
            prev.map((jobCode) =>
                jobCode.alias === record.alias ? { ...jobCode, [key]: value } : jobCode
            )
        );
    };

    const handleInputChange = (value: string | number, record: JobCode, key: keyof JobCode) => {
        setDirty(true);
        setData(prev => prev.map(item =>
            item.alias === record.alias ? { ...item, [key]: value } : item
        ));
    };

    const handleCheckboxChange = (checked: boolean, record: JobCode, key: keyof JobCode) => {
        setDirty(true);
        setData(prev => prev.map(item =>
            item.alias === record.alias ? { ...item, [key]: checked ? '1' : '0' } : item
        ));
    };

    const openModal = (record: JobCode) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const openSystemPlanUploadModal = (record: any) => {
        setSelectedRecord(record);
        setIsSystemPlanUploadModalVisible(true);
    };

    const isFormValid = (): boolean => {
        // Obligatorisch ist nur die Bezeichnung. Einbauort/Montageart werden beim Anlegen
        // mit sinnvollen Defaults vorbelegt (siehe handleCreateNewProduct), damit das Produkt
        // vollständig ist, ohne dass es dafür ein Pflichtfeld im Modal braucht.
        return !!newProduct.description?.trim();
    };

    const handleHeliosLinkInputChange = (value: string, setValue: (val: string) => void) => {
        if (value === "h") {
            setValue("helios://");
        } else if (value.startsWith("helios://") || (value.startsWith("helios://") && value.length > 9)) {
            setValue(value);
        } else  {
            message.error("Bitte gebe eine gültige Helios-URL an");
        }
    };

    const closeModal = () => {
        setSelectedRecord(null);
        setIsModalVisible(false);
    };

    const handleReset = async () => {
        if (selectedProject) {
            await fetchData(selectedProject);
            setDirty(false);
            message.success('Produkt-Daten erfolgreich zurückgesetzt.');
        }
    };

    const hasChanges = (): boolean => {
        return data.some((record, index) => {
            const original = originalData.find(o => o.alias === record.alias);
            return original && Object.keys(record).some(key => record[key as keyof JobCode] !== original[key as keyof JobCode]);
        });
    };

    const handleCreateNewProduct = () => {
        setNewProduct({
            alias: '',
            description: '',
            installationLocation: 'Innen',
            assemblyType: 'Eigene Montage',
            preAssembly: "0",
        });
        setIsNewProductModalVisible(true);
    };

    const handleModalSave = async () => {
        if (!newProduct.description) {
            message.error("Bitte Beschreibung ausfüllen.");
            return;
        }
        try {
            setIsSavingNewProduct(true);
            await axios.post(`${API_BASE_URL}/createNewProductForNetworkPlan`, {
                projectNo: selectedProject,
                jobCodes: [newProduct],
            });
            setIsNewProductModalVisible(false);
            if (selectedProject) fetchData(selectedProject);
        } catch (error) {
            console.error("Fehler beim Erstellen des Produkts:", error);
            message.error("Fehler beim Erstellen des Produkts.");
        } finally {
            setIsSavingNewProduct(false);
        }
    };

    const handleSystemPlanUploadModalOk = async () => {
        if (!heliosSystemPlansUrl || !selectedRecord) return;
        setIsSystemPlanSaving(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/bookSignedSystemplanNetworkOrderByJobCode`,
                {
                    params: {
                        auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU',
                        heliosURL: heliosSystemPlansUrl,
                        projectNo: selectedProject,
                        jobCode: selectedRecord.alias,
                        releaseDate: releaseDate ? releaseDate.format('DD.MM.YYYY') : null,
                    },
                }
            );
            if (response.data) {
                message.success('Systemplanfreigabe erfolgreich gebucht!');
                setReleaseDate(null);
                setHeliosSystemPlansUrl('');
                setIsSystemPlanUploadModalVisible(false);
                if (selectedProject) fetchData(selectedProject);
            }
        } catch (error) {
            console.error('Fehler:', error);
            message.error('Fehler beim Buchen der Systemplanfreigabe.');
        } finally {
            setIsSystemPlanSaving(false);
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/saveChangedNetworkPlanJobCodes`,
                { projectNo: selectedProject, data }
            );
            if (response.status === 200) {
                setOriginalData([...data]);
                setDirty(false);
                message.success('Produkt-Daten erfolgreich gespeichert.');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data || 'Fehler beim Speichern.';
            Modal.error({
                title: 'Fehler beim Speichern',
                content: <div dangerouslySetInnerHTML={{ __html: errorMessage }} />,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const renderCell = (value: any, record: JobCode, key: keyof JobCode, renderInput: (val: any) => React.ReactNode) => (
        <div style={{ backgroundColor: isCellChanged(record, key) ? '#d9f7be' : 'transparent', padding: '4px', borderRadius: '4px' }}>
            {renderInput(value)}
        </div>
    );

    const columns: ColumnsType<JobCode> = [
        {
            title: 'Code',
            dataIndex: 'alias',
            key: 'alias',
            sorter: (a, b) => a.alias.localeCompare(b.alias),
            render: (value, record) => (
                <Space>
                    {value}
                    {record.relationWarningText && (
                        <Tooltip overlayInnerStyle={{ maxWidth: 400 }} title={<div dangerouslySetInnerHTML={{ __html: record.relationWarningText }} />}>
                            <span className="warning-icon"><WarningOutlined /></span>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Bemerkung',
            width: 400,
            dataIndex: 'description',
            render: (value, record) => renderCell(value, record, 'description', val => (
                <Tooltip title={val}>
                    <Input disabled={record.planningProduct === '1'} value={val} onChange={e => handleInputChange(e.target.value, record, 'description')} />
                </Tooltip>
            )),
        },
        {
            title: <Tooltip title="Stunden in der Projektleitung"><span>PL h</span></Tooltip>,
            dataIndex: 'projectManagerHours',
            width: 120,
            render: (v, r) => renderCell(v, r, 'projectManagerHours', val => <Input disabled={r.planningProduct === '1'} type="number" value={val} onChange={e => handleInputChange(parseFloat(e.target.value), r, 'projectManagerHours')} />),
        },
        {
            title: <Tooltip title="Stunden in der Systemplanung"><span>SP h</span></Tooltip>,
            dataIndex: 'productOwnerHours',
            width: 120,
            render: (v, r) => renderCell(v, r, 'productOwnerHours', val => <Input disabled={r.planningProduct === '1'} type="number" value={val} onChange={e => handleInputChange(parseFloat(e.target.value), r, 'productOwnerHours')} />),
        },
        {
            title: <Tooltip title="Stunden in der Konstruktion"><span>Kon h</span></Tooltip>,
            dataIndex: 'drawingHours',
            width: 120,
            render: (v, r) => renderCell(v, r, 'drawingHours', val => <Input disabled={r.planningProduct === '1'} type="number" value={val} onChange={e => handleInputChange(parseFloat(e.target.value), r, 'drawingHours')} />),
        },
        {
            title: <Tooltip title="Manntage in der Montage"><span>Mont. MT</span></Tooltip>,
            dataIndex: 'assemblyDays',
            width: 120,
            render: (v, r) => renderCell(v, r, 'assemblyDays', val => <Input disabled={r.planningProduct === '1'} type="number" value={val} onChange={e => handleInputChange(parseFloat(e.target.value), r, 'assemblyDays')} />),
        },
        {
            title: 'Systempl.',
            dataIndex: 'hasSystemPlanning',
            width: 130,
            render: (v, r) => renderCell(v, r, 'hasSystemPlanning', val => <Checkbox disabled={r.planningProduct === '1'} checked={val === '1'} onChange={e => handleCheckboxChange(e.target.checked, r, 'hasSystemPlanning')} />),
        },
        {
            title: <Tooltip title="Zeichnen nach Theorie"><span>ZnT</span></Tooltip>,
            dataIndex: 'drawByTheory',
            width: 100,
            render: (v, r) => renderCell(v, r, 'drawByTheory', val => <Checkbox disabled={r.planningProduct === '1'} checked={val === '1'} onChange={e => handleCheckboxChange(e.target.checked, r, 'drawByTheory')} />),
        },
        {
            title: <Tooltip title="Handelsprodukt"><span>HP</span></Tooltip>,
            dataIndex: 'externalProduct',
            width: 100,
            render: (v, r) => renderCell(v, r, 'externalProduct', val => <Checkbox disabled={r.planningProduct === '1'} checked={val === '1'} onChange={e => handleCheckboxChange(e.target.checked, r, 'externalProduct')} />),
        },
        {
            title: <Tooltip title="Planungsprodukt"><span>PP</span></Tooltip>,
            dataIndex: 'planningProduct',
            width: 100,
            render: (v, r) => renderCell(v, r, 'planningProduct', val => <Checkbox checked={val === '1'} onChange={e => handleCheckboxChange(e.target.checked, r, 'planningProduct')} />),
        },
        {
            title: 'Verzinkt',
            dataIndex: 'galvanized',
            width: 130,
            render: (v, r) => renderCell(v, r, 'galvanized', val => <Checkbox disabled={r.planningProduct === '1'} checked={val === '1'} onChange={e => handleCheckboxChange(e.target.checked, r, 'galvanized')} />),
        },
        {
            title: 'Beschichtung',
            dataIndex: 'hasSurface',
            width: 130,
            render: (v, r) => renderCell(v, r, 'hasSurface', val => <Checkbox disabled={r.planningProduct === '1'} checked={val === '1'} onChange={e => handleCheckboxChange(e.target.checked, r, 'hasSurface')} />),
        },
        {
            title: "Vormontage",
            dataIndex: "preAssembly",
            width: 130,
            render: (v, r) => <Checkbox disabled={r.planningProduct === '1'} checked={v === "1"} onChange={(e) => handleCheckboxChange(e.target.checked, r, "preAssembly")} />,
        },
        {
            title: "Montage",
            dataIndex: "assemblyType",
            width: 170,
            render: (v, r) => (
                <Select disabled={r.planningProduct === '1'} style={{ width: "100%" }} value={v || "Eigene Montage"} onChange={(value) => handleDropdownChange(value, r, "assemblyType")}>
                    <Option value="Eigene Montage">Eigene Montage</Option>
                    <Option value="Fremdmontage">Fremdmontage</Option>
                    <Option value="Eigen- und Fremdmontage">Eigen- und Fremdmontage</Option>
                    <Option value="Nur Lieferung">Nur Lieferung</Option>
                </Select>
            ),
        },
        {
            title: 'Farbcode',
            dataIndex: 'colorCode',
            width: 200,
            render: (value, record) => (
                <Select
                    disabled={record.planningProduct === '1'}
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Farben eingeben"
                    value={value ? value.split('|') : []}
                    onChange={vals => handleInputChange(vals.join('|'), record, 'colorCode')}
                    open={false}
                    tagRender={({ label, closable, onClose }) => {
                        const fullLabel = label?.toString() ?? '';
                        const shortLabel = fullLabel.length > 10 ? fullLabel.slice(0, 10) + '…' : fullLabel;
                        return (
                            <Tooltip title={fullLabel}>
                                <Tag closable={closable} onClose={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span>{shortLabel}</span>
                                    <CopyOutlined onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(fullLabel); message.success('Kopiert!'); }} style={{ cursor: 'pointer', fontSize: 12 }} />
                                </Tag>
                            </Tooltip>
                        );
                    }}
                />
            ),
        },
        {
            title: '',
            key: 'document',
            width: 140,
            render: (_, record) => {
                const isConfigured = record.executionDataExisting;
                const hasDocuments = record.systemPlanningApprovalDate || record.heliosSystemplan || record.heliosFacadeplan || record.heliosSystemplanTemplate || record.heliosStatics || record.heliosArchitectPlans || record.heliosApprovedSystemPlan;
                return (
                    <Space>
                        <Tooltip title={hasDocuments ? "Dokumente anzeigen" : "Keine Dokumente"}><Button icon={<FileOutlined/>} disabled={!hasDocuments} onClick={() => openModal(record)}/></Tooltip>
                        <Tooltip title="Systemplan hochladen"><Button icon={<FileAddOutlined/>} disabled={!!record.heliosApprovedSystemPlan || record.hasSystemPlanning !== "1"} onClick={() => openSystemPlanUploadModal(record)}/></Tooltip>
                        {SHOW_PRODUCT_CONFIG_BUTTON && (
                            <Tooltip title={isConfigured ? "Konfiguration bearbeiten" : "Produkt konfigurieren"}>
                                <Button
                                    icon={<CodeSandboxOutlined />}
                                    onClick={() => openWizardModal(record)}
                                    style={{
                                        borderColor: isConfigured ? '#52c41a' : undefined,
                                        color: isConfigured ? '#52c41a' : undefined,
                                        backgroundColor: isConfigured ? '#f6ffed' : undefined
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'flex-end' }}>
                <Button icon={<UndoOutlined />} onClick={handleReset}>Daten neu laden</Button>
                <Button type="primary" icon={<FileAddOutlined />} onClick={handleCreateNewProduct}>Neues Produkt erstellen</Button>
                <Button type="primary" icon={<SaveOutlined />} disabled={!hasChanges()} onClick={handleSave}>Speichern</Button>
            </Space>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="alias"
                pagination={{ pageSize: 100 }}
                loading={isLoading}
                rowClassName={(record) => record.relationWarningText ? 'relation-warning-row' : record.planningProduct === '1' ? 'planning-product-row' : ''}
            />

            <Modal title="Verlinkte Helios-Dokumente" open={isModalVisible} footer={<Button type="primary" onClick={closeModal}>Schliessen</Button>} onCancel={closeModal}>
                {selectedRecord && (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                        {Object.entries(selectedRecord)
                            .filter(([key, value]) => ['systemPlanningApprovalDate', 'heliosSystemplan', 'heliosFacadeplan', 'heliosSystemplanTemplate', 'heliosStatics', 'heliosArchitectPlans', 'heliosApprovedSystemPlan', 'heliosSituationPlan'].includes(key) && value)
                            .map(([key, value]) => (
                                <tr key={key}>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}><strong>{key}</strong></td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{key.includes('Date') ? value : <a href={value as string} target="_blank" rel="noopener noreferrer">{value}</a>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Modal>

            <Modal title="Neues Produkt erstellen" open={isNewProductModalVisible} onCancel={() => setIsNewProductModalVisible(false)} footer={[
                <Button key="cancel" onClick={() => setIsNewProductModalVisible(false)} disabled={isSavingNewProduct}>Abbrechen</Button>,
                <Button key="submit" type="primary" onClick={handleModalSave} disabled={!isFormValid() || isSavingNewProduct} loading={isSavingNewProduct}>Speichern</Button>
            ]}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <label><strong>Bezeichnung<span style={{color: 'red'}}> *</span></strong></label>
                    <Input value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} />
                    <Checkbox checked={newProduct.hasSystemPlanning === '1'} onChange={e => setNewProduct({...newProduct, hasSystemPlanning: e.target.checked ? '1' : '0'})}>Systempl.</Checkbox>
                    <Checkbox checked={newProduct.drawByTheory === '1'} onChange={e => setNewProduct({...newProduct, drawByTheory: e.target.checked ? '1' : '0'})}>Zeichnen nach Theorie</Checkbox>
                    <Checkbox checked={newProduct.externalProduct === '1'} onChange={e => setNewProduct({...newProduct, externalProduct: e.target.checked ? '1' : '0'})}>Handelsprodukt</Checkbox>
                    <Checkbox checked={newProduct.galvanized === '1'} onChange={e => setNewProduct({...newProduct, galvanized: e.target.checked ? '1' : '0'})}>Verzinkt</Checkbox>
                    <Checkbox checked={newProduct.hasSurface === '1'} onChange={e => setNewProduct({...newProduct, hasSurface: e.target.checked ? '1' : '0'})}>Beschichtung</Checkbox>
                    <Checkbox checked={newProduct.preAssembly === '1'} onChange={e => setNewProduct({...newProduct, preAssembly: e.target.checked ? '1' : '0'})}>Vormontage</Checkbox>
                    <label><strong>Montage</strong></label>
                    <Select style={{width: '100%'}} value={newProduct.assemblyType} onChange={v => setNewProduct({...newProduct, assemblyType: v})}>
                        <Option value="Eigene Montage">Eigene Montage</Option>
                        <Option value="Nur Lieferung">Nur Lieferung</Option>
                    </Select>
                </Space>
            </Modal>

            <Modal title="Systemplan hochladen" open={isSystemPlanUploadModalVisible} onOk={handleSystemPlanUploadModalOk} onCancel={() => setIsSystemPlanUploadModalVisible(false)} footer={[
                <Button key="cancel" onClick={() => setIsSystemPlanUploadModalVisible(false)} style={{ backgroundColor: 'red', color: 'white' }}>Abbrechen</Button>,
                <Button key="submit" type="primary" disabled={!heliosSystemPlansUrl || !releaseDate} loading={isSystemPlanSaving} onClick={handleSystemPlanUploadModalOk}>Speichern</Button>,
            ]}>
                <Input placeholder="Helios-URL" value={heliosSystemPlansUrl} onChange={(e) => handleHeliosLinkInputChange(e.target.value, setHeliosSystemPlansUrl)} style={{ marginBottom: '15px' }} />
                <DatePicker locale={locale} style={{ width: '100%' }} value={releaseDate} onChange={setReleaseDate} format="DD.MM.YYYY" />
            </Modal>

            <Modal title={null} open={isIFrameModalVisible} onCancel={() => setIsIFrameModalVisible(false)} width="96vw" footer={null} centered destroyOnClose maskClosable={false} bodyStyle={{ padding: 0, height: '90vh', overflow: 'hidden' }} style={{ top: 20 }}>
                {selectedRecord && (
                    <iframe
                        // HIER IST DER FIX: encodeURIComponent + schließende Klammer }
                        // Token an den iframe durchreichen: kalkulation.muessig.app hat eine
                        // STRICT-Origin-Policy (JWT Pflicht, kein Legacy-Fallback). Ohne ?token
                        // hätte der iframe wegen Browser-Storage-Partitioning u.U. keinen Token
                        // und würde sofort ausgeloggt. Die Kalk-App liest ?token via bootstrapAuthFromURL.
                        src={`${KALKULATION_APP_URL}/embed/execution-wizard/${selectedProject}/${selectedRecord.alias}/${encodeURIComponent(selectedRecord.description || '')}?token=${encodeURIComponent(getToken() ?? '')}`}
                        style={{width: '100%', height: '100%', border: 'none', display: 'block'}}
                        title="Kalkulations-Wizard"
                    />
                )}
            </Modal>
        </div>
    );
};

export default JobCodeConfiguration;